import { authErrorSchema } from "@esi/shared";
import { KeyRound } from "lucide-react";
import { type ReactNode, useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { needsReauth } from "../api-errors.ts";
import { authErrorMessage } from "../shell/SignIn.tsx";
import { getPasskey, webAuthnSupported } from "../shell/webauthn.ts";

/**
 * The step-up shared by the tenant account page and the console profile: the password, the
 * authenticator code when the server asks for it, or a passkey. It knows no API: each caller
 * passes its own re-authentication calls (the account's, or the operator's), so the console
 * bundle never reaches a tenant route.
 */

/** What a re-authentication call answers (a `fetch` response, or the Hono client's). */
export interface StepUpResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

export interface StepUpCalls {
  /** The password, with the authenticator code once the server asked for one. */
  password: (body: { password: string; code?: string }) => Promise<StepUpResponse>;
  /** A passkey step-up, when the account may use one: its options, then the assertion. */
  passkey?: {
    options: () => Promise<StepUpResponse>;
    verify: (credential: Awaited<ReturnType<typeof getPasskey>>) => Promise<StepUpResponse>;
  };
}

/** Runs a security action; asks for a step-up first when the server wants one. */
export type Secure = (action: () => Promise<void>) => Promise<void>;

/**
 * Holds a security action the server refused for want of a fresh step-up (`409
 * reauth_required`), so the step-up dialog can run it again once the person confirmed.
 */
export function useSecureAction() {
  const [retry, setRetry] = useState<(() => Promise<void>) | null>(null);
  const secure = useCallback<Secure>(async (action) => {
    try {
      await action();
    } catch (error) {
      if (!needsReauth(error)) {
        throw error;
      }
      setRetry(() => action);
    }
  }, []);
  return {
    secure,
    /** A step-up is wanted. */
    asking: retry !== null,
    /** Ask for a step-up with nothing to run after it (to reveal what it guards). */
    ask: () => setRetry(() => async () => undefined),
    cancel: () => setRetry(null),
    /** The step-up succeeded: the held action, to run again through `secure`. */
    take: () => {
      const action = retry;
      setRetry(null);
      return action;
    },
  };
}

/** The step-up dialog's frame: its title, then the form or the caller's alternative. */
export function StepUpFrame(props: {
  open: boolean;
  onClose: () => void;
  description: string;
  returnFocus?: () => HTMLElement | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("stepUp.title")}
      description={props.description}
      returnFocus={props.returnFocus}
    >
      {props.children}
    </AppDialog>
  );
}

type Answer = { next?: string; options?: Record<string, unknown> };

export function StepUpForm(props: {
  calls: StepUpCalls;
  /** Offer "Use a passkey" up front (the server may still ask for one after the password). */
  offerPasskey?: boolean;
  /**
   * With `offerPasskey`: the passkey is the first choice (#405, the console's step-up for an
   * operator with a passkey), the password and code below it.
   */
  passkeyFirst?: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { calls } = props;
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function answer(res: StepUpResponse) {
    const body = (await res.json().catch(() => null)) as Answer | null;
    if (res.ok && body?.next === "done") {
      props.onDone();
      return null;
    }
    if (res.ok && body?.next === "totp") {
      setNeedsCode(true);
      setError(null);
      return null;
    }
    if (res.ok && body?.next === "passkey" && body.options) {
      return body.options;
    }
    const parsed = authErrorSchema.safeParse(body);
    setError(authErrorMessage({ status: res.status, error: parsed.success ? parsed.data : null }));
    return null;
  }

  async function submit() {
    setBusy(true);
    try {
      const options = await answer(
        await calls.password({ password, ...(needsCode ? { code } : {}) }),
      );
      // The account has a passkey: the password alone is not enough, the passkey follows.
      if (options) {
        await assertPasskey(options);
      }
    } catch {
      setError(t("errors.network"));
    } finally {
      setBusy(false);
    }
  }

  async function assertPasskey(options: Record<string, unknown>) {
    // No passkey step-up here (the console's code step-up): never start a useless ceremony.
    if (!calls.passkey) {
      setError(t("signIn.errors.passkeyCancelled"));
      return;
    }
    const credential = await getPasskey(options).catch(() => null);
    if (!credential) {
      setError(t("signIn.errors.passkeyCancelled"));
      return;
    }
    await answer(await calls.passkey.verify(credential));
  }

  async function passkey() {
    if (!calls.passkey) {
      return;
    }
    setBusy(true);
    try {
      const options = await answer(await calls.passkey.options());
      if (options) {
        await assertPasskey(options);
      }
    } catch {
      setError(t("errors.network"));
    } finally {
      setBusy(false);
    }
  }

  const passkeyOffered = Boolean(props.offerPasskey && calls.passkey && webAuthnSupported());
  const first = passkeyOffered && props.passkeyFirst === true;
  const passkeyButton = (
    <Button
      type="button"
      variant={first ? "default" : "outline"}
      className={first ? "w-full" : "self-start"}
      disabled={busy}
      // The first choice takes focus when the dialog opens.
      autoFocus={first}
      onClick={() => void passkey()}
    >
      <KeyRound aria-hidden="true" strokeWidth={1.75} />
      {t("signIn.usePasskey")}
    </Button>
  );
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {first ? (
        <>
          {passkeyButton}
          <div className="flex items-center gap-3 text-xs text-fg-muted">
            <span aria-hidden="true" className="h-px flex-1 bg-line" />
            {t("stepUp.orPassword")}
            <span aria-hidden="true" className="h-px flex-1 bg-line" />
          </div>
        </>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-password`}>{t("password.current")}</Label>
        <Input
          id={`${id}-password`}
          type="password"
          autoComplete="current-password"
          required
          autoFocus={!first}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={error ? true : undefined}
        />
      </div>
      {needsCode ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-code`}>{t("totp.code")}</Label>
          <Input
            id={`${id}-code`}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]{6,7}"
            maxLength={7}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="max-w-40 font-mono tracking-widest"
          />
        </div>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      {passkeyOffered && !first ? passkeyButton : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={props.onClose}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {t("stepUp.confirm")}
        </Button>
      </DialogFooter>
    </form>
  );
}
